import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { Badge, Col, Row } from 'antd'
import { ActionButton } from 'Components/button'
import { selectCurrentUser } from 'Store/user/selector'
import EventModal from 'Components/modal/EventModal'
import LoadingView from 'Components/modal/LoadingView'
import { DetailDrawer } from 'Components/common/MainLayout'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { InfoItem } from 'Pages/Orders/components'
import PosTerminal from '../../assets/img/pos-terminal.png'
import { CloseOutlined } from '@ant-design/icons'
import { getPosMornitoringDetailAPI, regeneratePosAPI } from 'Store/pos-mornitoring/api'
import { DetailReq, UpdateReq, TerminalData } from 'Store/pos-mornitoring/types'
import { selectDetail, selectDetailEvent, selectActionEvent } from 'Store/pos-mornitoring/selector'
import { clearEventAction } from 'Store/pos-mornitoring/actions'
import { formatDateTime, formatTimeAgo } from 'helpers/format'
import { ConfirmDailog } from 'Components/modal'

type Props = ReduxProps & {
  visible: boolean
  onCloseDrawer: () => void
  onSuccess?: () => void
  id?: number
}

const Detail: React.FunctionComponent<Props> = props => {
  const {
    onCloseDrawer,
    visible,
    currentUser,
    onSuccess = () => {},
    id = 0,
    detail,
    detailEvent,
    actionEvent,
  } = props

  const dispatch = useDispatch()
  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState(false)
  const [terminalID, setTerminalID] = useState(0)

  const onGetDetail = (): void => {
    const req: DetailReq = {
      id,
    }
    dispatch(getPosMornitoringDetailAPI(req, currentUser.accessToken))
  }

  const onRegenerate = (id: number): void => {
    const req: UpdateReq = {
      id,
    }
    dispatch(regeneratePosAPI(req, currentUser.accessToken))
  }

  useEffect(() => {
    if (visible) {
      onGetDetail()
    }
  }, [visible])

  const clearEvent = (): void => {
    dispatch(clearEventAction())
  }

  const onEventSuccess = (): void => {
    clearEvent()
    onSuccess()
  }

  const onConfirmRegenerate = () => {
    setConfirmDeleteDialog(false)
    onRegenerate(terminalID)
  }

  const onClickRegenerate = (id: number) => {
    setConfirmDeleteDialog(true)
    setTerminalID(id)
  }

  const renderInfo = (title: string, value: any, span: number = 15) => {
    return <InfoItem title={title} value={value} span={span} />
  }

  const ConfirmDeleteModal = (
    <ConfirmDailog
      title={`ยืนยันการลบ?`}
      description={`คุณแน่ใจหรือไม่ที่จะลบ Terminal นี้ออกจากระบบ?`}
      confirmBtnTxt="ยืนยัน"
      isShow={confirmDeleteDialog}
      handleOk={onConfirmRegenerate}
      handleCancel={() => setConfirmDeleteDialog(false)}
    />
  )

  const merchantInfo = () => {
    return (
      <BoxWrapper>
        <Row gutter={[16, 16]}>
          <Col md={24} xs={24}>
            <BoxTitle>ข้อมูลร้านค้า</BoxTitle>
            {renderInfo('ชื่อร้าน', detail?.merchant.name || '')}
            {renderInfo('ที่อยู่', detail?.merchant.address || '')}
            {renderInfo('เบอร์โทรศัพท์', detail?.merchant.owner_phone || '')}
            {renderInfo('อีเมล', detail?.merchant.email || '')}
            {renderInfo('แพ็กเกจที่ใช้', detail?.package.name || '')}
            {renderInfo('วันหมดอายุแพ็กเกจ', formatDateTime(detail?.package.end_date || ''))}
          </Col>
        </Row>
      </BoxWrapper>
    )
  }

  const deviceStatus = (online: boolean) => {
    return (
      <span>
        <Badge status={online ? 'success' : 'error'} />
        {online ? 'ออนไลน์' : 'ออฟไลน์'}
      </span>
    )
  }

  const deviceInfo = (terminal: TerminalData) => {
    return (
      <Row gutter={[48, 16]} align="middle" justify="space-between">
        <Col span={21}>
          <BoxWrapper>
            <Row gutter={[16, 16]} align="middle" justify="space-between">
              <Col md={6} xs={24}>
                <CenterText>
                  <img src={PosTerminal} width="40" />
                  <TerminalID>Terminal</TerminalID>
                  <TerminalID>{terminal.name}</TerminalID>
                </CenterText>
              </Col>
              <Col md={18} xs={24}>
                {renderInfo('IP Address', terminal.ip, 12)}
                {renderInfo('หมายเลขเครื่อง', terminal.serial_no, 12)}
                {renderInfo('สถานะเครื่อง', deviceStatus(terminal.online), 12)}
                {renderInfo('เคลื่อนไหวล่าสุด', formatTimeAgo(terminal.lasted_at), 12)}
              </Col>
            </Row>
          </BoxWrapper>
        </Col>
        <Col span={3}>
          <ActionButton
            text=""
            icon={<CloseOutlined />}
            label="Re-generate"
            action="edit"
            onClick={() => onClickRegenerate(terminal.id)}
          />
        </Col>
      </Row>
    )
  }

  const title = detail?.merchant.name || ''
  const terminals = detail?.package?.terminals || []

  return (
    <DetailDrawer
      title={title}
      onCloseDrawer={onCloseDrawer}
      visible={visible}
      footer={null}
      maskClosable={true}
      width={'50%'}
    >
      <LoadingView isShow={detailEvent.IsLoading} />
      <EventModal
        event={[actionEvent]}
        enable={true}
        successCalback={onEventSuccess}
        errorCalback={clearEvent}
      />
      <Row justify="space-between" gutter={[16, 8]}>
        <Col span={24}>{merchantInfo()}</Col>
        {terminals.length > 0 && (
          <Col span={24}>
            <BoxTitle>ข้อมูลเครื่อง</BoxTitle>
          </Col>
        )}
        {terminals.map((item: TerminalData, index: number) => {
          return (
            <Col lg={24} xs={24} key={index}>
              {deviceInfo(item)}
            </Col>
          )
        })}
      </Row>
      {ConfirmDeleteModal}
    </DetailDrawer>
  )
}

const BoxTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.primary};
  margin-bottom: 5px;
`

const BoxWrapper = styled.div`
  width: 100%;
  border-radius: 10px;
  padding: 15px 10px;
  //border: 1px solid ${(props: { theme: MyTheme }): string => props.theme.colors.whitebg};
  border: 1px solid #f0f0f0;
  margin-bottom: 10px;
  background-color:  ${(props: { theme: MyTheme }): string => props.theme.colors.white};
`

const TerminalID = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.primary};
`

const CenterText = styled.div`
  text-align: center;
`

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    detail: selectDetail(state),
    detailEvent: selectDetailEvent(state),
    actionEvent: selectActionEvent(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(Detail)
